import api from '../config/api';

export const authService = {
  async login(username, password) {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },

  async register(userData) {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  async getCurrentUser() {
    const response = await api.get('/auth/me');
    return response.data;
  },

  async forgotPassword(email) {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  async resetPassword(token, newPassword) {
    const response = await api.post('/auth/reset-password', {
      token,
      new_password: newPassword
    });
    return response.data;
  },

  logout() {
    localStorage.removeItem('abell_token');
    localStorage.removeItem('abell_user');
  }
};

export const dashboardService = {
  async getStats() {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },

  async getRecentActivities(limit = 10) {
    const response = await api.get(`/dashboard/recent-activities?limit=${limit}`);
    return response.data;
  },

  async getProductionChart() {
    const response = await api.get('/dashboard/charts/production');
    return response.data;
  },

  async getStockChart() {
    const response = await api.get('/dashboard/charts/stock');
    return response.data;
  }
};

export const externalService = {
  async getExchangeRates() {
    const response = await api.get('/external/exchange-rates');
    return response.data;
  },

  async getLMEPrices() {
    const response = await api.get('/external/lme-prices');
    return response.data;
  }
};

export const projectService = {
  async getAll() {
    const response = await api.get('/projects');
    return response.data;
  },

  async getActiveWithParts() {
    const response = await api.get('/projects/active-with-parts');
    return response.data;
  },

  async create(projectData) {
    const response = await api.post('/projects', projectData);
    return response.data;
  },

  async update(id, projectData) {
    const response = await api.put(`/projects/${id}`, projectData);
    return response.data;
  },

  async delete(id) {
    const response = await api.delete(`/projects/${id}`);
    return response.data;
  },

  async importExcel(formData) {
    const response = await api.post('/projects/import/excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }
};

export const stockService = {
  async getAll() {
    const response = await api.get('/stock');
    return response.data;
  },

  async create(stockData) {
    const response = await api.post('/stock', stockData);
    return response.data;
  },

  async update(id, stockData) {
    const response = await api.put(`/stock/${id}`, stockData);
    return response.data;
  },

  async delete(id) {
    const response = await api.delete(`/stock/${id}`);
    return response.data;
  },

  async exportExcel(filters = {}) {
    const params = new URLSearchParams();
    if (filters.material_name) params.append('material_name', filters.material_name);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    
    const response = await api.get(`/stock/export/excel?${params.toString()}`, {
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `stok_raporu_${new Date().getTime()}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  async exportPDF(filters = {}) {
    const params = new URLSearchParams();
    if (filters.material_name) params.append('material_name', filters.material_name);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    
    const response = await api.get(`/stock/export/pdf?${params.toString()}`, {
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `stok_raporu_${new Date().getTime()}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  async importExcel(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/stock/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }
};

export const productionService = {
  async getAll() {
    const response = await api.get('/production');
    return response.data;
  },

  async create(productionData) {
    const response = await api.post('/production', productionData);
    return response.data;
  },

  async exportExcel(filters = {}) {
    const params = new URLSearchParams();
    if (filters.project) params.append('project', filters.project);
    if (filters.part_name) params.append('part_name', filters.part_name);
    if (filters.material_type) params.append('material_type', filters.material_type);
    if (filters.operator) params.append('operator', filters.operator);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    
    const response = await api.get(`/production/export/excel?${params.toString()}`, {
      responseType: 'blob'
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `uretim_raporu_${new Date().getTime()}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  async exportPDF(filters = {}) {
    const params = new URLSearchParams();
    if (filters.project) params.append('project', filters.project);
    if (filters.part_name) params.append('part_name', filters.part_name);
    if (filters.material_type) params.append('material_type', filters.material_type);
    if (filters.operator) params.append('operator', filters.operator);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    
    const response = await api.get(`/production/export/pdf?${params.toString()}`, {
      responseType: 'blob'
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `uretim_raporu_${new Date().getTime()}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  async importExcel(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/production/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }
};

export const shipmentService = {
  async getAll() {
    const response = await api.get('/shipment');
    return response.data;
  },

  async create(shipmentData) {
    const response = await api.post('/shipment', shipmentData);
    return response.data;
  },

  async exportExcel(filters = {}) {
    const params = new URLSearchParams();
    if (filters.project) params.append('project', filters.project);
    if (filters.source_location) params.append('source_location', filters.source_location);
    if (filters.destination) params.append('destination', filters.destination);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    
    const response = await api.get(`/shipment/export/excel?${params.toString()}`, {
      responseType: 'blob'
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `sevkiyat_raporu_${new Date().getTime()}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  async exportPDF(filters = {}) {
    const params = new URLSearchParams();
    if (filters.project) params.append('project', filters.project);
    if (filters.source_location) params.append('source_location', filters.source_location);
    if (filters.destination) params.append('destination', filters.destination);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    
    const response = await api.get(`/shipment/export/pdf?${params.toString()}`, {
      responseType: 'blob'
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `sevkiyat_raporu_${new Date().getTime()}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  async importExcel(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/shipment/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }
};

export const userPasswordService = {
  async adminResetPassword(userId, newPassword = null, sendEmail = true) {
    const response = await api.post(`/users/${userId}/reset-password`, {
      new_password: newPassword,
      send_email: sendEmail
    });
    return response.data;
  }
};

export const smtpService = {
  async getSettings() {
    const response = await api.get('/smtp/settings');
    return response.data;
  },

  async saveSettings(settings) {
    const response = await api.post('/smtp/settings', settings);
    return response.data;
  },

  async updateSettings(settings) {
    const response = await api.put('/smtp/settings', settings);
    return response.data;
  },

  async deleteSettings() {
    const response = await api.delete('/smtp/settings');
    return response.data;
  },

  async testConnection() {
    const response = await api.post('/smtp/test');
    return response.data;
  }
};

export const mailService = {
  async sendStockMail(recipients, attachExcel, attachPdf, filters = {}) {
    const response = await api.post('/stock/send-mail', {
      recipients,
      subject: 'ABELL - Stok Raporu',
      attach_excel: attachExcel,
      attach_pdf: attachPdf,
      filters
    });
    return response.data;
  },

  async sendProductionMail(recipients, attachExcel, attachPdf, filters = {}) {
    const response = await api.post('/production/send-mail', {
      recipients,
      subject: 'ABELL - Üretim Raporu',
      attach_excel: attachExcel,
      attach_pdf: attachPdf,
      filters
    });
    return response.data;
  },

  async sendShipmentMail(recipients, attachExcel, attachPdf, filters = {}) {
    const response = await api.post('/shipment/send-mail', {
      recipients,
      subject: 'ABELL - Sevkiyat Raporu',
      attach_excel: attachExcel,
      attach_pdf: attachPdf,
      filters
    });
    return response.data;
  }
};

export const reportsService = {
  async getStockSummary(filters = {}) {
    const params = new URLSearchParams();
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.location) params.append('location', filters.location);
    
    const response = await api.get(`/reports/stock-summary?${params.toString()}`);
    return response.data;
  },

  async getProductionPerformance(filters = {}) {
    const params = new URLSearchParams();
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.project) params.append('project', filters.project);
    if (filters.operator) params.append('operator', filters.operator);
    
    const response = await api.get(`/reports/production-performance?${params.toString()}`);
    return response.data;
  },

  async getShipmentAnalysis(filters = {}) {
    const params = new URLSearchParams();
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.project) params.append('project', filters.project);
    if (filters.source_location) params.append('source_location', filters.source_location);
    if (filters.destination) params.append('destination', filters.destination);
    
    const response = await api.get(`/reports/shipment-analysis?${params.toString()}`);
    return response.data;
  },

  async getGeneralStatistics(filters = {}) {
    const params = new URLSearchParams();
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    
    const response = await api.get(`/reports/general-statistics?${params.toString()}`);
    return response.data;
  }
};

export const trackingService = {
  async getStockStatus(filters = {}) {
    const params = new URLSearchParams();
    if (filters.material_type) params.append('material_type', filters.material_type);
    if (filters.material_quality) params.append('material_quality', filters.material_quality);
    if (filters.stock_status) params.append('stock_status', filters.stock_status);
    if (filters.material_name) params.append('material_name', filters.material_name);
    
    const response = await api.get(`/tracking/stock-status?${params.toString()}`);
    return response.data;
  },

  async exportExcel(filters = {}) {
    const params = new URLSearchParams();
    if (filters.material_type) params.append('material_type', filters.material_type);
    if (filters.material_quality) params.append('material_quality', filters.material_quality);
    if (filters.stock_status) params.append('stock_status', filters.stock_status);
    if (filters.material_name) params.append('material_name', filters.material_name);
    
    const response = await api.get(`/tracking/export/excel?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  async exportPdf(filters = {}) {
    const params = new URLSearchParams();
    if (filters.material_type) params.append('material_type', filters.material_type);
    if (filters.material_quality) params.append('material_quality', filters.material_quality);
    if (filters.stock_status) params.append('stock_status', filters.stock_status);
    if (filters.material_name) params.append('material_name', filters.material_name);
    
    const response = await api.get(`/tracking/export/pdf?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  async getMachineProduction(filters = {}) {
    const params = new URLSearchParams();
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.makina) params.append('makina', filters.makina);
    if (filters.operator) params.append('operator', filters.operator);
    
    const response = await api.get(`/tracking/machine-production?${params.toString()}`);
    return response.data;
  },

  async exportMachineProductionExcel(filters = {}) {
    const params = new URLSearchParams();
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.makina) params.append('makina', filters.makina);
    if (filters.operator) params.append('operator', filters.operator);
    
    const response = await api.get(`/tracking/machine-production/export/excel?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  async exportMachineProductionPdf(filters = {}) {
    const params = new URLSearchParams();
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.makina) params.append('makina', filters.makina);
    if (filters.operator) params.append('operator', filters.operator);
    
    const response = await api.get(`/tracking/machine-production/export/pdf?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  }
};

export const materialSettingsService = {
  async getSettings() {
    const response = await api.get('/materials/settings');
    return response.data;
  },

  async createSetting(setting) {
    const response = await api.post('/materials/settings', setting);
    return response.data;
  },

  async updateSetting(materialName, update, materialType = null, materialQuality = null) {
    const params = new URLSearchParams();
    if (materialType) params.append('material_type', materialType);
    if (materialQuality) params.append('material_quality', materialQuality);
    
    const response = await api.put(`/materials/settings/${materialName}?${params.toString()}`, update);
    return response.data;
  },

  async deleteSetting(materialName, materialType = null, materialQuality = null) {
    const params = new URLSearchParams();
    if (materialType) params.append('material_type', materialType);
    if (materialQuality) params.append('material_quality', materialQuality);
    
    const response = await api.delete(`/materials/settings/${materialName}?${params.toString()}`);
    return response.data;
  }
};

export const excelService = {
  async importExcel(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/excel/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }
};